/**
 * Clean DOCX-imported and legacy blog HTML before rendering.
 * Fixes nested anchors, missing bullets, broken image paths, and Explore more CTAs.
 */
import { rewriteArticleLinks } from './rewriteArticleLinks';

const NESTED_ANCHOR_RE =
    /<a\b[^>]*href="([^"]*?)<a\b[^>]*href="([^"]*)"[^>]*>([^<]*)<\/a>([^"]*?)"[^>]*>([^<]*)<\/a>/gi;

const VISIBLE_ATTR_FRAGMENT_RE =
    /(?<!<a\b[^>]{0,400})target="_blank"\s+rel="noopener noreferrer">/gi;

const EMPTY_CTA_RE =
    /<p>\s*(?:👉\s*)?(?:Explore more|Learn more|Try the Playground)[^<]*:\s*<\/p>/gi;

const EMPTY_EXPLORE_ARROW_RE = /<p>\s*Explore more\s*→\s*<\/p>/gi;

const SIGNOFF_PARA_RE =
    /<p>\s*(?:<strong>)?\s*(?:✉️|📞|Thanks for reading)[\s\S]*?(?:<\/strong>)?\s*<\/p>\s*/gi;

const CONTACT_SIGNOFF_RE =
    /<p>\s*(?:Email\s*(?:&nbsp;|\s)+Phone|Email|Phone)\s*<\/p>\s*(?:<p>[\s\S]*?(?:mailto:|tel:|support@)[\s\S]*?<\/p>\s*)+/gi;

// Single-paragraph only — do not use [\s\S] across </p> or the whole article is removed.
const TRAILING_CONTACT_PARA_RE =
    /<p>(?:(?!<\/p>).)*(?:Email\s*:|Phone\s*:|mailto:|tel:|support@|support-)(?:(?!<\/p>).)*<\/p>\s*$/gi;

const EMPTY_HREF_ANCHOR_RE = /<a\b[^>]*href="\s*"\s*[^>]*>[\s\S]*?<\/a>/gi;

const HASH_HREF_ANCHOR_RE = /<a\b[^>]*href="#"\s*[^>]*>[\s\S]*?<\/a>/gi;

const INTERNAL_BLANK_TARGET_RE =
    /<a\b([^>]*?)href="(\/(?!\/)[^"]*)"([^>]*?)\s*target="_blank"\s+rel="noopener noreferrer"([^>]*)>/gi;

const BLOCK_SPLIT_RE = /(?=<(?:p|h[1-4]|ul|ol|img)\b)/i;

const EXPLORE_MORE_SECTION_RE =
    /<h3>\s*🌐\s*Explore more:\s*<\/h3>([\s\S]*?)(?=<h[1-3]\b|$)/gi;

const BARE_URL_RE = /\b(?:https?:\/\/)?(?:www\.)?([a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/[^\s<]*)?)/gi;

const EXPLORE_SITE_MAP = [
    { pattern: /safevaultusa|safevault\.com/i, label: 'SafeVault USA', pickUrl: (siteUrls) => siteUrls.safevaultHome || siteUrls.marketingHome },
    { pattern: /amitasoft/i, label: 'AmitaSoft', pickUrl: (siteUrls) => siteUrls.amitasoftHome },
    { pattern: /cliqloan/i, label: 'CliQloan', pickUrl: (siteUrls) => siteUrls.cliqloanHome },
];

function repairNestedAnchors(html) {
    let out = html;
    let prev;
    do {
        prev = out;
        out = out.replace(NESTED_ANCHOR_RE, (_match, _start, innerHref, _innerText, _end, outerText) => {
            const text = outerText.trim() || _innerText.trim();
            return `<a href="${innerHref}">${text}</a>`;
        });
    } while (out !== prev);
    return out;
}

function stripHtmlText(value) {
    return String(value || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

function isListIntro(text) {
    const t = stripHtmlText(text);
    if (!t) {
        return false;
    }
    if (/[:：]$/.test(t)) {
        return true;
    }
    return /(?:including|such as|typically include|still moving|often experience|should become|that means|relying on|usually have|creates|either|for example|real-time dashboard|key performance metrics|actually happening)$/i.test(t);
}

function isStrongListIntro(text) {
    const t = stripHtmlText(text);
    return /[:：]$/.test(t);
}

function isListItemText(text, strictIntro = false) {
    const t = stripHtmlText(text);
    if (!t) {
        return false;
    }
    if (t.toLowerCase() === 'or') {
        return true;
    }
    if (strictIntro) {
        if (
            /^(?:Instead|A |An |The |With |When |Meanwhile|Because|For |In |As |This |These |Those |Businesses |Companies |Clients |Teams )\b/i.test(
                t,
            )
        ) {
            return false;
        }
        if (t.length > 120) {
            return false;
        }
        return true;
    }
    if (t.length > 180) {
        return false;
    }
    const sentenceCount = (t.match(/[.!?](?:\s|$)/g) || []).length;
    if (sentenceCount >= 2 && t.length > 90) {
        return false;
    }
    return true;
}

function convertParagraphListsToBullets(html) {
    const parts = html.split(BLOCK_SPLIT_RE).filter(Boolean);
    const output = [];

    for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index];
        const introMatch = part.match(/^<(?:p|h[2-4])>([\s\S]*?)<\/(?:p|h[2-4])>\s*$/i);

        if (!introMatch || !isListIntro(introMatch[1])) {
            output.push(part);
            continue;
        }

        const strictIntro = isStrongListIntro(introMatch[1]);
        const items = [];
        let cursor = index + 1;

        while (cursor < parts.length) {
            const paragraphMatch = parts[cursor].match(/^<p>([\s\S]*?)<\/p>\s*$/i);
            if (!paragraphMatch) {
                break;
            }
            if (!isListItemText(paragraphMatch[1], strictIntro)) {
                break;
            }
            items.push(paragraphMatch[1].trim());
            cursor += 1;
        }

        if (items.length < 2) {
            output.push(part);
            continue;
        }

        const listHtml = items
            .map((item) => {
                const plain = stripHtmlText(item);
                if (plain.toLowerCase() === 'or') {
                    return '<li class="blog-list-or">or</li>';
                }
                return `<li>${item}</li>`;
            })
            .join('');

        output.push(part, `<ul class="blog-bullet-list">${listHtml}</ul>`);
        index = cursor - 1;
    }

    return output.join('');
}

function repairImageSrcPaths(html, publicUrlPrefix = '/blog') {
    const prefix = publicUrlPrefix.replace(/\/$/, '');

    return html.replace(/<img\b([^>]*?)\bsrc="([^"]+)"/gi, (_match, attrs, src) => {
        let fixed = src.replace(/\\/g, '/');
        const cleanAttrs = attrs
            .replace(/\s(?:width|height|style)=["'][^"']*["']/gi, '')
            .replace(/\sclass=["'][^"']*["']/gi, '');

        if (/^https?:\/\//i.test(fixed)) {
            return `<img${cleanAttrs} class="blog-inline-image" src="${fixed}" loading="lazy" decoding="async"`;
        }

        if (fixed.startsWith(`${prefix}/`)) {
            return `<img${cleanAttrs} class="blog-inline-image" src="${fixed}" loading="lazy" decoding="async"`;
        }

        if (fixed.startsWith('/blogImages/')) {
            fixed = `${prefix}${fixed}`;
        } else if (fixed.startsWith('blogImages/')) {
            fixed = `${prefix}/${fixed}`;
        } else if (fixed.startsWith('/images/')) {
            fixed = `${prefix}${fixed}`;
        } else if (fixed.startsWith('images/')) {
            fixed = `${prefix}/${fixed}`;
        } else if (fixed.startsWith('/')) {
            fixed = `${prefix}${fixed}`;
        } else {
            fixed = `${prefix}/images/${fixed.replace(/^images\//, '')}`;
        }

        return `<img${cleanAttrs} class="blog-inline-image" src="${fixed}" loading="lazy" decoding="async"`;
    });
}

function stripLeadingTitle(html, title) {
    if (!title) {
        return html;
    }

    const normalizedTitle = stripHtmlText(title).toLowerCase();
    let out = html;
    for (const tag of ['h1', 'h2']) {
        const headingRe = new RegExp(`^\\s*<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}>\\s*`, 'i');
        out = out.replace(headingRe, (match) => {
            const headingText = stripHtmlText(match).toLowerCase();
            if (headingText === normalizedTitle || headingText.startsWith(normalizedTitle.slice(0, 40))) {
                return '';
            }
            return match;
        });
    }
    return out;
}

function stripLeadingImages(html, count = 1) {
    let out = html;
    for (let i = 0; i < count; i += 1) {
        out = out.replace(/^\s*<img\b[^>]*\/?>\s*/i, '');
    }
    return out;
}

function linkifyBareUrls(text, siteUrls) {
    return text.replace(BARE_URL_RE, (match, hostPath) => {
        const host = hostPath.split('/')[0].toLowerCase();
        for (const entry of EXPLORE_SITE_MAP) {
            if (entry.pattern.test(host)) {
                const href = entry.pickUrl(siteUrls);
                if (href) {
                    return `<a href="${href}">${entry.label}</a>`;
                }
            }
        }
        const href = match.startsWith('http') ? match : `https://${match.replace(/^www\./, '')}`;
        return `<a href="${href}">${match}</a>`;
    });
}

function parseExploreMoreLinks(sectionBody, siteUrls) {
    const links = [];
    const seen = new Set();
    const paragraphs = [...sectionBody.matchAll(/<p>([\s\S]*?)<\/p>/gi)].map((match) => stripHtmlText(match[1]));

    paragraphs.forEach((text) => {
        if (!text) {
            return;
        }

        if (/try the playground/i.test(text)) {
            const href = siteUrls.safevaultHome || siteUrls.marketingHome;
            if (href && !seen.has(href)) {
                seen.add(href);
                links.push({ href, label: 'SafeVault USA — Try the Playground' });
            }
            return;
        }

        for (const entry of EXPLORE_SITE_MAP) {
            if (entry.pattern.test(text)) {
                const href = entry.pickUrl(siteUrls);
                if (href && !seen.has(href)) {
                    seen.add(href);
                    links.push({ href, label: `${entry.label} — ${text.replace(/^https?:\/\//, '')}` });
                }
                return;
            }
        }

        if (BARE_URL_RE.test(text)) {
            BARE_URL_RE.lastIndex = 0;
            const linked = linkifyBareUrls(text, siteUrls);
            const hrefMatch = linked.match(/href="([^"]+)"/);
            const labelMatch = linked.match(/>([^<]+)</);
            if (hrefMatch && labelMatch && !seen.has(hrefMatch[1])) {
                seen.add(hrefMatch[1]);
                links.push({ href: hrefMatch[1], label: labelMatch[1] });
            }
        }
    });

    if (!links.length && (siteUrls.safevaultHome || siteUrls.marketingHome)) {
        const fallbackHref = siteUrls.marketingHome || siteUrls.safevaultHome;
        const fallbackHost = fallbackHref.replace(/^https?:\/\//, '').replace(/\/$/, '');
        links.push({
            href: fallbackHref,
            label: `AmitaSoft — ${fallbackHost}`,
        });
    }

    return links;
}

function repairExploreMoreSection(html, siteUrls) {
    return html.replace(EXPLORE_MORE_SECTION_RE, (_match, body) => {
        const links = parseExploreMoreLinks(body, siteUrls);
        if (!links.length) {
            return '';
        }

        const items = links
            .map((link) => `<li><a href="${link.href}" target="_blank" rel="noopener noreferrer">${link.label}</a></li>`)
            .join('');

        return `<h3>🌐 Explore more:</h3><ul class="blog-explore-more">${items}</ul>`;
    });
}

function stripImportedSignOff(html) {
    let out = html;
    let prev;
    do {
        prev = out;
        out = out.replace(CONTACT_SIGNOFF_RE, '');
        out = out.replace(SIGNOFF_PARA_RE, '');
        out = out.replace(TRAILING_CONTACT_PARA_RE, '');
    } while (out !== prev);
    return out.trim();
}

function fixLinkPunctuation(html) {
    return html.replace(/<\/a><strong>,<\/strong>/g, '</a>,');
}

function unwrapDraftLinks(html) {
    return html.replace(
        /<a\b[^>]*href="https?:\/\/docs\.google\.com[^"]*"[^>]*>([\s\S]*?)<\/a>/gi,
        '$1',
    );
}

function stripTrackingParams(html) {
    return html.replace(/(<a\b[^>]*href=")([^"]+)("[^>]*>)/gi, (match, pre, href, post) => {
        try {
            const url = new URL(href);
            ['utm_source', 'utm_medium', 'utm_campaign'].forEach((key) => url.searchParams.delete(key));
            const cleaned = url.search ? url.toString() : `${url.origin}${url.pathname}${url.hash}`;
            return `${pre}${cleaned}${post}`;
        } catch {
            return match;
        }
    });
}

function fixCollapsedWords(html) {
    return html
        .replace(/That['\u2019]swhere/gi, "That's where ")
        .replace(/<\/strong>([a-z])/gi, '</strong> $1')
        .replace(/<\/a>([a-z])/gi, '</a> $1')
        .replace(/([a-z])<a\b/gi, '$1 <a');
}

function sanitizeArticleLinks(html) {
    let out = html;
    out = unwrapDraftLinks(out);
    out = stripTrackingParams(out);
    out = fixLinkPunctuation(out);
    out = fixCollapsedWords(out);
    return out;
}

function linkifyStandaloneDomains(html, siteUrls) {
    return html.replace(/<p>\s*((?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)+)\s*<\/p>/gi, (match, domain) => {
        if (/<a\b/i.test(match)) {
            return match;
        }
        const linked = linkifyBareUrls(domain, siteUrls);
        if (linked === domain) {
            return match;
        }
        return `<p>${linked}</p>`;
    });
}

export function normalizeArticleHtml(html, siteUrls, options = {}) {
    if (!html || typeof html !== 'string') {
        return '';
    }

    const {
        stripTitle = '',
        stripLeadingImages: leadingImagesToStrip = 0,
        publicUrlPrefix = process.env.PUBLIC_URL || '/blog',
    } = options;

    let out = html;

    out = repairNestedAnchors(out);
    out = out.replace(VISIBLE_ATTR_FRAGMENT_RE, '');
    out = out.replace(EMPTY_CTA_RE, '');
    out = out.replace(EMPTY_EXPLORE_ARROW_RE, '');
    out = out.replace(EMPTY_HREF_ANCHOR_RE, '');
    out = out.replace(HASH_HREF_ANCHOR_RE, '');
    out = out.replace(INTERNAL_BLANK_TARGET_RE, '<a$1href="$2"$3$4>');
    out = stripLeadingTitle(out, stripTitle);
    out = stripLeadingImages(out, leadingImagesToStrip);
    out = repairImageSrcPaths(out, publicUrlPrefix);
    out = convertParagraphListsToBullets(out);
    out = repairExploreMoreSection(out, siteUrls);
    out = linkifyStandaloneDomains(out, siteUrls);
    out = stripImportedSignOff(out);
    out = sanitizeArticleLinks(out);
    out = rewriteArticleLinks(out, siteUrls);

    return out.trim();
}

export default normalizeArticleHtml;
